import { format, parse } from "date-fns";
import {
  ReactNode,
  SetStateAction,
  createContext,
  useEffect,
  useState,
} from "react";

import config from "../config";
import { TileOverlayContextState } from "./TileOverlayContext";

// NOAA RIDGE2 WMS Service Configuration
const RIDGE2_WMS_URL =
  "https://mapservices.weather.noaa.gov/eventdriven/services/radar/radar_base_reflectivity_time/ImageServer/WMSServer";
const RIDGE2_REST_URL =
  "https://mapservices.weather.noaa.gov/eventdriven/rest/services/radar/radar_base_reflectivity_time/ImageServer";
const RIDGE2_LAYER = "radar_base_reflectivity_time";

// Number of frames and interval between them (in minutes)
const FRAME_COUNT = 10;
const FRAME_INTERVAL_MINUTES = 10;

export interface RadarTimeInfo {
  wmsUrl: string;
  layer: string;
  times: Date[];
}

export interface RadarLegacyInfo {
  nullTile: string;
  baseURL: string;
  times: Date[];
}

export interface RadarContextState extends TileOverlayContextState {
  timeInfo?: RadarTimeInfo;
  legacyInfo?: RadarLegacyInfo;
  times?: Date[];
  setFramesLoaded: (count: number) => void;
}

const RadarContext = createContext<RadarContextState | undefined>(undefined);

export const RadarContextProvider = ({ children }: { children: ReactNode }) => {
  const [frame, setFrame] = useState<number>(0);
  const [speed, setSpeed] = useState<number>(500);

  const [playing, setPlaying] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);

  const [opacity, setOpacity] = useState<number>(0.5);

  const [timeInfo, setTimeInfo] = useState<RadarTimeInfo>();
  const [legacyInfo, setLegacyInfo] = useState<RadarLegacyInfo>();

  const [framesLoaded, setFramesLoaded] = useState<number>(0);

  const useLegacy = config.radarLegacy === true;

  const [legacyApiUrl, setLegacyApiUrl] = useState<string>(
    process.env.REACT_APP_API_URL || "https://jefdaq.jefulleralert.com"
  );

  // Fetch RIDGE2 WMS data (new default)
  useEffect(() => {
    if (useLegacy) return;
    const update = () =>
      fetchRidge2TimeExtent()
        .then((endTime) => {
          const times: Date[] = [];
          for (let i = 0; i < FRAME_COUNT; i++) {
            const frameTime = new Date(
              endTime.getTime() - i * FRAME_INTERVAL_MINUTES * 60 * 1000
            );
            times.push(frameTime);
          }
          setTimeInfo({
            wmsUrl: RIDGE2_WMS_URL,
            layer: RIDGE2_LAYER,
            times,
          });
        })
        .catch((e: any) => {
          console.error("Failed to fetch RIDGE2 time extent:", e);
        });
    update();
    const interval = setInterval(update, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [useLegacy]);

  // Fetch legacy JEF API radar data
  useEffect(() => {
    if (!useLegacy) return;
    const update = () =>
      fetchLegacyRadarAPI(legacyApiUrl)
        .then(setLegacyInfo)
        .catch((e: any) => {
          if (!legacyApiUrl.includes("jefulleralert")) {
            setLegacyApiUrl("https://jefdaq.jefulleralert.com");
          } else {
            console.error(e);
          }
        });
    update();
    const interval = setInterval(update, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [useLegacy, legacyApiUrl]);

  const times = timeInfo?.times ?? legacyInfo?.times;
  const frameCount = times?.length ?? FRAME_COUNT;

  useLoopFrames(playing ? speed : 0, frame, !visible, frameCount, setFrame);

  const state: RadarContextState = {
    timeInfo,
    legacyInfo,
    times,
    frame,
    playing,
    setPlaying,
    visible,
    setVisible,
    speed,
    setSpeed,
    opacity,
    setOpacity,
    framesLoaded,
    framesTotal: frameCount,
    setFramesLoaded,
  };
  return (
    <RadarContext.Provider value={state}>{children}</RadarContext.Provider>
  );
};

// Fetch the current time extent from RIDGE2 ImageServer REST API
const fetchRidge2TimeExtent = async (): Promise<Date> => {
  const response = await fetch(`${RIDGE2_REST_URL}?f=json`);
  const data = await response.json();
  const endTime = data.timeInfo?.timeExtent?.[1];
  if (!endTime) {
    throw new Error("No time extent found in RIDGE2 response");
  }
  return new Date(endTime);
};

// Convert Date to ISO8601 format for WMS TIME parameter
export const toWmsTimeFormat = (time: Date): string => {
  return time.toISOString();
};

// Legacy JEF API radar fetch
const legacyApiTimeFormat = "yyyyMMdd'_'HHmmss";
export const toLegacyApiTimeFormat = (time: Date) =>
  format(time, legacyApiTimeFormat);

const fetchLegacyRadarAPI = (apiUrl: string): Promise<RadarLegacyInfo> => {
  return fetch(apiUrl + "/json/radar/")
    .then((res) => res.json())
    .then((result) => {
      const d0q = result["D0Q"];
      const times = d0q["times"].map((t: string) =>
        parse(t, legacyApiTimeFormat, new Date())
      );
      return { baseURL: d0q.baseURL, nullTile: d0q.nullTile, times };
    });
};

const useLoopFrames = (
  speed: number,
  frame: number,
  reset: boolean,
  frameCount: number,
  setFrame: (number: SetStateAction<number>) => void
) => {
  useEffect(() => {
    const updateFrame = () =>
      setFrame((frame) => (frame === 0 ? frameCount - 1 : frame - 1));

    let interval: NodeJS.Timeout | null = null;
    if (reset) {
      setFrame(0);
    } else if (speed > 0) {
      const delta = (1000 - speed) * (frame === 0 ? 4 : 1);
      interval = setInterval(updateFrame, delta);
    } else {
      interval && clearInterval(interval);
    }
    return () => {
      interval && clearInterval(interval);
    };
  }, [frame, frameCount, reset, setFrame, speed]);
};

export default RadarContext;
